import React, { useContext, useEffect, useState } from 'react';
import { Switch, Route, useRouteMatch, Link, useHistory } from 'react-router-dom';
import { ResidentChart } from './features/resident-medications/ResidentChart';
import { PharmacyChart } from './features/pharmacy-medications/PharmacyChart';
import { ResidentList } from './features/resident-list/ResidentList';
import { QueryParams } from './features/common/constants';
import { SplashScreen } from './features/splash-screen/SplashScreen';
import { AuthenticationError } from './features/splash-screen/authentication-error/AuthenticationError';
import { IdentityContext } from './features/auth/UserIdentity';
import { useIdentity } from './features/auth/useIdentity';
import { RenderIf } from './features/common/render-if/RenderIf';
import { AuthorizedPatientRoute } from './features/common/route-extensions/AuthorizedRoute';
import { LinkButton, TabsComponent } from './features/common';
import { MedpointMedicationList } from './features/medication-list';
import { DoctorRoute } from './features/common/route-extensions/DoctorRoute';
import { UserType } from './services/medicationmanagement/medsUser/MedsUserApi';
import { makeStyles } from '@material-ui/core';
import { medsUserApi } from './services/medicationmanagement/medsUser/MedsUserApi';
import { useAsync } from './features/common/async';
import { SharedUserPage } from './features/shared-user/SharedUserPage';
import { configureMedicationSettings, getFromStorage } from './config/auth.config';
import { MapUserToExternalUser } from './features/splash-screen/MapUserToExternalUser';
import { DraftMedicationDialogBox } from './features/common/dialogbox/DraftMedicationDialogBox';
import TopbarBanner from './features/topbar-banner/TopbarBanner';
import { getApiConfig } from './config/api.config';

const useStyle = makeStyles({
    inheritFormat: {
        textDecoration: 'inherit',
        color: 'inherit'
    }
});

export const AutoLinkUserToExternalUser = async (userName: string) => {
    return await medsUserApi.autoLinkUserToExternalUser(userName);
};

export const Bootstrapper = () => {
    let styles = useStyle();
    let { loading, identity, error } = useIdentity();
    let ctx = useContext(IdentityContext);
    let { path } = useRouteMatch();
    const [hasDraftMedications, setHasDraftMedications] = useState(false);
    const [showDraftPrompt, setShowDraftPrompt] = useState(false);
    let { inProgress, success: autoUserLink, error: autoUserLinkError, queryDispatcher } = useAsync();
    const history = useHistory();
    const [randomKey, setRandomKey] = useState('');
    const [lmsApiError, setLmsApiError] = useState(false);
    const [lmsLink, setLmsLink] = useState(null);
    const [lmsLinkReady, setLmsLinkReady] = useState(false);

    const showPrescriptionPolicyBanner = getApiConfig()?.topbarFlags?.showPrescriptionPolicyBanner;

    useEffect(() => {
        if (identity != null && !identity?.userType) {
            queryDispatcher(AutoLinkUserToExternalUser(identity?.userName));
        }
    }, [identity]);

    useEffect(() => {
        if (localStorage.getItem('lmsLink') != null) {
            setLmsLink(JSON.parse(localStorage.getItem('lmsLink') ?? ''));
        }
        if (localStorage.getItem('lmsApiError') != null) {
            setLmsApiError(localStorage.getItem('lmsApiError') == 'true' ? true : false);
        }
    }, [lmsLinkReady]);

    const onLmsLinkReady = (val: any) => {
        setLmsLinkReady(val);
    };
    const getDraftMedicationFlag = (flag: boolean) => {
        setHasDraftMedications(flag);
    };

    const populateHeaderLinks = () => {
        let headerContent: JSX.Element[] = [];
        headerContent.push(
            <a className={styles.inheritFormat} onClick={() => residentsClick()}>
                Residents
            </a>
        );

        //Added '1 != 1' to hide the 'Medication list' tab in 1.1 release. Just remove this condition  to show 'Medication list' tab.
        if (
            1 != 1 &&
            (ctx?.userInfo?.userType === UserType.Doctor ||
                ctx?.userInfo?.userType === UserType.Dentist ||
                ctx?.userInfo?.userType === UserType.GeneralStaffAdmin ||
                ctx?.userInfo?.userType === UserType.NursePractitioner ||
                ctx?.userInfo?.userType === UserType.Optometrist ||
                ctx?.userInfo?.userType === UserType.RegisteredNurse)
        ) {
            headerContent.push(
                <Link className={styles.inheritFormat} to={{ pathname: `${path}/medications-list` }}>
                    Medication list
                </Link>
            );
        }
        return headerContent;
    };

    function residentsClick() {
        setRandomKey(createKey);
        if (!hasDraftMedications) {
            history.push('/residents');
            setShowDraftPrompt(false);
        }
        setShowDraftPrompt(hasDraftMedications);
    }

    const getTopbarContent = () => {
        return (
            <>
                Medications Management has achieved eNRMC conformance. All existing and new orders in MedPoint remain valid, <b>no action is required from prescribers or pharmacies at this stage</b>.
                {' '}Further updates will be shared as we transition to the fully eNRMC‑conformant version.
            </>
        );
    };

    //Prompt dailog is triggering only once and not triggering if user clicks on cancel. So, passing random value to retrigger
    const createKey = () => {
        return Date.now() + Math.random().toString(36).substring(2, 9);
    };

    if (loading) {
        return <SplashScreen noAnimation key='loading-screen' />;
    } else if (identity != null && !identity?.userType) {
        if (autoUserLink) {
            window.location.reload();
        } else if (autoUserLinkError) {
            let errorMessage = '';
            try {
                const errorObject = JSON.parse(autoUserLinkError);
                errorMessage = errorObject?.content[0]?.propertyName;
                if (errorMessage == 'ExternalUser_Already_Mapped' || errorMessage == 'User_Already_Mapped')
                    return <AuthenticationError errorMessage={errorMessage} />;
                else return <MapUserToExternalUser userId={identity.userId} isAnotherProvider={false} />;
            } catch (e) {
                return <AuthenticationError errorMessage={errorMessage} />;
            }
        } else {
            return <MapUserToExternalUser userId={identity.userId} isAnotherProvider={false} />;
        }
    } else if (identity?.isShared && !getFromStorage('providerId')) {
        return <SharedUserPage />;
    }
    if (error) {
        return <AuthenticationError errorMessage='' />;
    } else {
        //configureMedicationSettings used be in index.tsx.
        //But now moved here as it needs UserManger in which new parameter isClinicalIdentityProviderUser is added
        configureMedicationSettings(identity?.isClinicalIdentityProviderUser);

        return (
            <RenderIf if={!!ctx?.userInfo} else={<SplashScreen noAnimation key='loading-screen' />}>
                <>
                    <DraftMedicationDialogBox
                        when={showDraftPrompt}
                        onOK={() => true}
                        onCancel={() => false}
                        key={randomKey}
                    />

                    {showPrescriptionPolicyBanner && <TopbarBanner content={getTopbarContent()} />}

                    <TabsComponent headers={populateHeaderLinks()} content={[]} />
                    <Switch>
                        <AuthorizedPatientRoute
                            path={`${path}/gp/:${QueryParams.AuthoringSystem}/:${QueryParams.AuthoringSystemForeignKey}`}
                        >
                            <ResidentChart
                                hasDraftMedications={getDraftMedicationFlag}
                                lmsLink={lmsLink}
                                lmsApiError={lmsApiError}
                            />
                        </AuthorizedPatientRoute>
                        <AuthorizedPatientRoute
                            path={`${path}/pharmacy/:${QueryParams.AuthoringSystem}/:${QueryParams.AuthoringSystemForeignKey}`}
                        >
                            <PharmacyChart lmsLink={lmsLink} lmsApiError={lmsApiError} />
                        </AuthorizedPatientRoute>
                        <DoctorRoute path={`${path}/medications-list`}>
                            <MedpointMedicationList />
                        </DoctorRoute>
                        <Route path={path}>
                            <ResidentList
                                onLmsLinkReady={onLmsLinkReady}
                                onLmsLink={setLmsLink}
                                onLmsApiError={setLmsApiError}
                            ></ResidentList>
                        </Route>
                    </Switch>
                </>
            </RenderIf>
        );
    }
};
